import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, Typography } from "@mui/material";
import { useOutletContext } from "react-router-dom";

import useDataSourceManager from "hooks/useDataSourceManager";

import Loading from "../loading";
import EditMemberUser from "pages/System/UserManagement/MspUserManagement/Drawer/EditUser/Member";
import DeleteUserModal from "pages/System/UserManagement/MspUserManagement/Modal/DeleteUser";
import { MSP_ROLE_HIERARCHY, ORG_ROLE_HIERARCHY } from "const/userManagement";

const ROLE_ID_BY_NAME = Object.fromEntries(
  MSP_ROLE_HIERARCHY.map(({ mspRoleId, mspRoleName }) => [mspRoleName, mspRoleId]),
);

const formatStatus = (status) => status
  ? `${status.charAt(0).toUpperCase()}${status.slice(1).toLowerCase()}`
  : status;

const getInitials = (name) => name
  ?.split(" ")
  .map((word) => word[0])
  .join("")
  .toUpperCase() ?? "?";

const MspUserManagement = ({ entId, userId, closeDrawerCallback }) => {
  const { scopeData = {} } = useOutletContext();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [usersUuid] = useState(uuidv4());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);

  const breadcrumbs = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    } catch {
      return {};
    }
  }, []);
  const roleName = breadcrumbs.realm?.roleName ?? null;
  const roleId = breadcrumbs.realm?.roleId ?? ROLE_ID_BY_NAME[roleName] ?? null;
  const isViewer = roleName === "Viewer" || roleName === "None";
  const allOrgNames = useMemo(() => (scopeData.orgs ?? [])
    .map((org) => ({
      orgId: org.id,
      orgName: org.name,
      orgRoleId: org.roleId,
      orgRoleName: org.roleName,
    }))
    .sort((a, b) => a.orgName.localeCompare(b.orgName)), [scopeData.orgs]);

  const getListEnterpriseUsers = () => {
    if (entId) sendRequest("/ents/:entId/users.read", usersUuid, { entId });
  };

  useEffect(() => {
    if (!entId || !userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getListEnterpriseUsers();
  }, [entId, userId]);

  useEffect(() => {
    const response = getData(usersUuid);
    if (!response) return;

    const user = (response.data?.users ?? []).find((item) => item.id === userId) ?? null;
    setOriginalUser(user);
    setSelectedUser(user);
    setIsLoading(false);
  }, [dataMap, userId]);

  if (isLoading) return <Loading />;

  if (!selectedUser) {
    return <Box p={3}><Typography>User information is unavailable.</Typography></Box>;
  }

  return (
    <>
      <EditMemberUser
        setSelectedRowData={setSelectedUser}
        selectedRowData={selectedUser}
        drawerOpen
        setDrawerOpen={closeDrawerCallback}
        getInitials={getInitials}
        setOpenDeleteAccountModal={setOpenDeleteAccountModal}
        allOrgNames={allOrgNames}
        orgRoleHierarchy={ORG_ROLE_HIERARCHY}
        mspRoleHierarchy={MSP_ROLE_HIERARCHY}
        entId={entId}
        getListEnterpriseUsers={getListEnterpriseUsers}
        getListPendingEnterpriseUsers={() => {}}
        currentUserMspRoleId={roleId}
        setCurrentUserMspRoleId={() => {}}
        setCurrentUserMspRoleName={() => {}}
        isViewer={isViewer}
        formatStatus={formatStatus}
        originalRowData={originalUser}
      />
      <DeleteUserModal
        openDeleteAccountModal={openDeleteAccountModal}
        handleCloseDeleteAccountModal={() => setOpenDeleteAccountModal(false)}
        selectedRowData={selectedUser}
        entId={entId}
        getListEnterpriseUsers={getListEnterpriseUsers}
        getListPendingEnterpriseUsers={() => {}}
        setDrawerOpen={closeDrawerCallback}
      />
    </>
  );
};

export default MspUserManagement;
